import { ArrowRight } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { EntityIdBadge } from '../../components/EntityIdBadge';
import { cn } from '../../lib/cn';
import { formatHebrewRelative } from '../../lib/relativeTime';
import { BotSubNav } from './BotSubNav';
import {
  botStateKind,
  displayState,
  SERVICE_LABELS_HE,
} from './bot.schema';
import { useBotConversation } from './botQueries';
import { BotActionsBar } from './components/BotActionsBar';
import { CapturedFieldsCard } from './components/CapturedFieldsCard';
import { ConversationTimeline } from './components/ConversationTimeline';
import { LeadLifecycleCard } from './components/LeadLifecycleCard';
import styles from './bot.module.css';

export function ConversationProfile() {
  const { id } = useParams<{ id: string }>();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: conv, isLoading } = useBotConversation(orgId, id ?? null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.notice}>טוען…</p>
      </div>
    );
  }
  if (!conv) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>השיחה לא נמצאה.</p>
        <Link to="/bot/conversations" className={styles.btnSecondary}>חזרה לרשימה</Link>
      </div>
    );
  }

  const kind = botStateKind(conv.state);
  const statePillClass =
    kind === 'human-takeover' ? styles.statePillHuman
    : kind === 'dormant' ? styles.statePillDormant
    : kind === 'closed' ? styles.statePillClosed
    : styles.statePillActive;

  return (
    <div className={styles.page}>
      <Link to="/bot/conversations" className={styles.btnSecondary} style={{ alignSelf: 'flex-start' }}>
        <ArrowRight size={14} aria-hidden="true" /> חזרה לשיחות
      </Link>

      <BotSubNav />

      <header className={styles.profileHeader}>
        <div className={styles.profileTopRow}>
          <div>
            <div className={styles.profilePhone}>
              <bdi dir="ltr">{conv.phone}</bdi>
            </div>
            <div className={styles.profileName}>
              {conv.full_name ?? 'ללא שם'}
              {conv.business_type ? ` · ${conv.business_type}` : ''}
              {conv.service_slug ? ` · ${SERVICE_LABELS_HE[conv.service_slug]}` : ''}
            </div>
            <EntityIdBadge id={conv.id} />
          </div>
          <div className={styles.profileActions}>
            <span className={cn(styles.statePill, statePillClass)}>
              {displayState(conv.state)}
            </span>
            <span className={styles.kpiLabel}>
              נראה לאחרונה {formatHebrewRelative(conv.last_message_at)}
            </span>
          </div>
        </div>
      </header>

      <BotActionsBar orgId={orgId} conversation={conv} />

      <div className={styles.twoCol}>
        <div className={styles.col}>
          <ConversationTimeline orgId={orgId} conversationId={conv.id} />
        </div>
        <div className={styles.col}>
          <LeadLifecycleCard orgId={orgId} leadId={conv.lead_id} />
          <CapturedFieldsCard conversation={conv} />
        </div>
      </div>
    </div>
  );
}
